import React from "react";

import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useTheme } from "@vapor/react-material/styles";
import IconButton from "@vapor/react-material/IconButton";
import CssBaseline from "@vapor/react-material/CssBaseline";
import Box from "@vapor/react-material/Box";
import { PopoverProps } from "@vapor/react-material";

import Bars from "@vapor/react-icons/Bars";
import AngleLeft from "@vapor/react-icons/AngleLeft";

import { library } from "@fortawesome/fontawesome-svg-core";
import { far as proRegularFar } from "@fortawesome/pro-regular-svg-icons";

import { Drawer } from "./UIShellDrawer";
import { FirstLevelListButtons } from "./FirstLevelMenu";

library.add(proRegularFar);

// const camelize = (s) => s.replace(/-./g, (x) => x[1].toUpperCase());

// === icons with React.lazy ===

// const LazyIcon = ({ icon }) => {
//   const Icon = React.lazy(() =>
//     import(`@fortawesome/pro-regular-svg-icons/${icon}.js`).then(
//       ({ definition }) => ({
//         default: () => (
//           <FontAwesomeIcon icon={definition} style={{ width: "16px" }} />
//         )
//       })
//     )
//   );

//   return <Icon />;
// };

// === icons with useEffect ===

// const LazyIcon = ({ icon }) => {
//   const [Icon, setIcon] = React.useState(null);

//   React.useEffect(() => {
//     import(`@fortawesome/pro-regular-svg-icons/${icon}.js`)
//       .then(({ definition }) => {
//         setIcon(() => () => (
//           <FontAwesomeIcon icon={definition} style={{ width: "16px" }} />
//         ));
//       })
//       .catch((error) => console.log(error));
//   }, [icon]);

//   if (!Icon) return null;

//   return <Icon />;
// };

type UIShellProps = {
  children?: React.ReactNode;
  renderToolbar?: React.ReactNode;
  renderDrawer?: React.ReactNode;
  drawerOpen?: boolean;
  setDrawerOpen?: Function;
  hideDrawer?: boolean;
  menuLevels?: Object | any;
};

export interface ExtendedPopoverProps extends PopoverProps {
  menuItem?: "menuItem";
}

const drawerWidth = 248;

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(6)} + 1px)`
  }
});

const DrawerXl: React.FC<
  React.ComponentProps<typeof Drawer> & { open?: boolean }
> = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open"
})<{ open?: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "none",
  ...((open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "248px !important",
      borderRight: "none"
    }
  }) as any),
  ...((!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "48px !important",
      borderRight: "none"
    }
  }) as any)
}));

export const VaporUIShellNav = (props: UIShellProps) => {
  const { renderToolbar, renderDrawer, hideDrawer } = props;
  const { drawerOpen, setDrawerOpen } = props;

  const theme: any = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const [selectedIndex, setSelectedIndex] = React.useState(null);

  const [sublevelSelectedIndex, setSublevelSelectedIndex] =
    React.useState(null);

  const [sublevel, setSublevel] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const slideRef = React.useRef(null);

  const sublevelMenuRef = React.useRef(null);

  React.useEffect(() => {
    hideDrawer && props.setDrawerOpen(false);
  }, []);

  React.useEffect(() => {
    setSelectedIndex(null);
    setSublevelSelectedIndex(null);
    setSublevel(null);
    handlePopoverClose();
  }, [props.menuLevels]);

  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  const showUpperLevel = (child) => {
    setSublevel(child.parent?.parent ? child.parent : undefined);
    setSublevelSelectedIndex(null);
    !child.parent?.parent && handlePopoverClose();
  };

  const transformRec = (ob, parent = undefined, ancestor = undefined) => {
    ob.parent = parent;
    ob.ancestor = ancestor;

    if (ob?.children) {
      for (let i = 0; i < ob.children.length; i++) {
        transformRec(ob.children[i], ob, ob.ancestor || i);
      }
    }
    return ob;
  };

  if (props.menuLevels) {
    transformRec(props.menuLevels);
  }

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Box>
      <Box sx={{ display: "flex" }} ref={sublevelMenuRef}>
        <CssBaseline />

        {renderToolbar || null}

        <UiShellDrawer
          drawerOpen={drawerOpen}
          hideDrawer={hideDrawer}
          toggleDrawer={toggleDrawer}
          hasToolbar={renderToolbar === undefined ? false : true}
          handlePopoverClose={handlePopoverClose}
          ref={slideRef}
        >
          <nav role="navigation" aria-label="Navigation Menu">
            {renderDrawer || null}
            {props.menuLevels && (
              <FirstLevelListButtons
                isLarge={isLarge}
                menuLevels={props.menuLevels}
                setSublevel={setSublevel}
                setSublevelSelectedIndex={setSublevelSelectedIndex}
                sublevelSelectedIndex={sublevelSelectedIndex}
                selectedIndex={selectedIndex}
                handlePopoverClick={handlePopoverClick}
                handlePopoverClose={handlePopoverClose}
                setSelectedIndex={setSelectedIndex}
                drawerOpen={drawerOpen}
                sublevel={sublevel}
                sublevelMenuRef={sublevelMenuRef}
                openPopover={openPopover}
                showUpperLevel={showUpperLevel}
                slideRef={slideRef}
              />
            )}
          </nav>
        </UiShellDrawer>

        <Box sx={{ flexGrow: 1, mt: renderToolbar ? "48px" : 0 }}>
          {props.children}
        </Box>
      </Box>
    </Box>
  );
};

type UiShellDrawerProps = {
  drawerOpen: boolean;
  hideDrawer: boolean;
  toggleDrawer: () => void;
  hasToolbar: boolean;
  children: React.ReactNode;
  handlePopoverClose: () => void;
};

const UiShellDrawer = React.forwardRef<HTMLDivElement, UiShellDrawerProps>(
  (
    {
      drawerOpen,
      hideDrawer,
      toggleDrawer,
      hasToolbar,
      children,
      handlePopoverClose
    },
    ref
  ) => {
    const theme: any = useTheme();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

    return (
      <>
        {(isLarge || !hasToolbar) && (
          <DrawerXl
            variant="permanent"
            open={drawerOpen}
            sx={{
              display: hideDrawer
                ? "none"
                : { xs: !hasToolbar ? "block" : "none", lg: "block" },
              overflowY: "hidden",
              zIndex: hideDrawer && theme.zIndex.drawer + 100
            }}
            ref={ref}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: drawerOpen ? "flex-end" : "center",
                padding: theme.spacing(0, 1),
                height: 48
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <IconButton
                onClick={toggleDrawer}
                role="button"
                sx={{
                  ":focus-visible": {
                    background: "#5A6872"
                  }
                }}
              >
                {drawerOpen ? (
                  <AngleLeft color="white" />
                ) : (
                  <Bars color="white" />
                )}
              </IconButton>
            </Box>
            <Box
              sx={{
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: 0,
                  background: "transparent"
                }
              }}
            >
              <>{children}</>
            </Box>
          </DrawerXl>
        )}

        {!isLarge && hasToolbar && (
          <Drawer
            anchor="left"
            variant="temporary"
            ModalProps={{
              onClose: () => {
                handlePopoverClose();
                toggleDrawer();
              },
              BackdropProps: {
                sx: { background: "rgba(0, 70, 102, 0.32) !important" }
              }
            }}
            sx={{
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                boxSizing: "border-box"
              },
              display: hideDrawer ? "none" : { xs: "block", lg: "none" },
              "& .MuiDrawer-paperAnchorLeft": {
                marginTop: "48px"
              },
              "& ::-webkit-scrollbar": {
                width: 0,
                background: "transparent"
              }
            }}
            open={drawerOpen}
            ref={ref}
          >
            <Box>{children}</Box>
          </Drawer>
        )}
      </>
    );
  }
);
